import { AlertCircle, ArrowRight, RefreshCw } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { z } from "zod";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaForm } from "@/shared/components/forms/ColocaForm";
import { ColocaOtpField } from "@/shared/components/forms/ColocaOtpField";
import { useZodForm } from "@/shared/components/forms/useZodForm";
import useTranslations from "@/shared/lang/useTranslations";
import { colocaToast } from "@/shared/utils/toast";

import { OTP_LENGTH, RESEND_COOLDOWN_SECONDS } from "../constants/register.constants";
import { useRegister } from "../hooks/useRegister";

interface VerifyEmailFormProps {
  email: string;
}

interface VerifyFormValues {
  code: string;
}

type RegisterTranslator = ReturnType<typeof useTranslations<"register">>["t"];

// Maps the backend's raw error string to a localized message. The nest auth
// service emits messages like "Invalid verification code" or
// "Verification code has expired"; we substring-match so the UI stays
// consistent even when the backend tweaks copy. Sentinel values set by the
// thunk (e.g. for HTTP 429) take precedence.
function translateVerifyError(raw: string | null | undefined, t: RegisterTranslator): string {
  if (!raw) return t("verify_error_generic");
  if (raw === "verify_error_generic") return t("verify_error_generic");
  if (raw === "verify_error_rate_limited") return t("verify_error_rate_limited");

  const lower = raw.toLowerCase();
  if (lower.includes("expired")) return t("verify_error_expired");
  if (lower.includes("already verified") || lower.includes("already_verified")) {
    return t("verify_error_already_verified");
  }
  if (lower.includes("too many") || lower.includes("rate") || lower.includes("throttle")) {
    return t("verify_error_rate_limited");
  }
  if (lower.includes("invalid") || lower.includes("incorrect")) {
    return t("verify_error_invalid_code");
  }
  return t("verify_error_generic");
}

export function VerifyEmailForm({ email }: VerifyEmailFormProps) {
  const { t } = useTranslations("register");
  const {
    isVerifying,
    verifyError,
    verified,
    isResending,
    resendError,
    lastResendAt,
    doVerifyEmail,
    doResendVerificationCode,
    clearVerifyError,
  } = useRegister();

  const schema = useMemo(
    () =>
      z.object({
        code: z.string().regex(new RegExp(`^\\d{${OTP_LENGTH}}$`), t("verify_error_invalid_code_format")),
      }),
    [t],
  );
  // Validated on submit only: the field is auto-focused, so "on touched" flagged an empty code as invalid the
  // moment focus moved elsewhere — e.g. to "Change" next to the address — before anyone tried to verify.
  const form = useZodForm<typeof schema>(schema, { code: "" }, "onSubmit");
  const { setValue, formState } = form;

  // Tick once per second so the cooldown countdown re-renders.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const cooldownRemaining = useMemo(() => {
    if (!lastResendAt) return 0;
    const elapsedSec = Math.floor((now - lastResendAt) / 1000);
    return Math.max(0, RESEND_COOLDOWN_SECONDS - elapsedSec);
  }, [lastResendAt, now]);

  const onSubmit = ({ code }: VerifyFormValues) => {
    clearVerifyError();
    doVerifyEmail({ email, code });
  };

  // Resend success: detect a fresh `lastResendAt` by comparing to the previous
  // value held in a ref. Triggers a toast and clears the previously-entered code.
  const prevResendAt = useRef<number | undefined>(lastResendAt);
  useEffect(() => {
    if (lastResendAt && lastResendAt !== prevResendAt.current) {
      colocaToast.success(t("verify_resend_sent"));
      setValue("code", "");
      clearVerifyError();
    }
    prevResendAt.current = lastResendAt;
  }, [lastResendAt, t, setValue, clearVerifyError]);

  // Surface resend errors as toasts so they don't crowd the verify banner.
  useEffect(() => {
    if (resendError) {
      colocaToast.error(translateVerifyError(resendError, t));
    }
  }, [resendError, t]);

  /**
   * On verify success: announce it, and nothing else.
   *
   * The redirect is deliberately NOT here. Leaving the screen is decided by `email_verified_at` from
   * `/auth/me`, and `UnverifiedOnlyRoute` already sends the user to /app the moment that field arrives — it
   * is the exact negation of the gate in `PrivateRoute` that holds them here. Redirecting from this form
   * instead raced that fetch: `thunkVerifyEmail` flips `register.verified` BEFORE awaiting `/auth/me`, so for
   * one round trip this component wanted /app while the gate still wanted this screen. Each bounce remounted
   * this component and re-fired this effect, two `history.replaceState` per cycle, until WebKit refused the
   * 101st in 10 seconds — and because react-router does not guard its `replace` path, that throw left the
   * router mid-navigation on a blank page (COLOCA-DASHBOARD-1J).
   *
   * A one-shot ref would not have fixed it: the loop remounts this component, so any per-mount ref resets
   * with it. The redirect needs one owner, and the owner is the guard that reads the server's answer.
   */
  useEffect(() => {
    if (verified) {
      colocaToast.success(t("verify_success_toast"));
    }
  }, [verified, t]);

  const handleResend = () => {
    if (cooldownRemaining > 0 || isResending) return;
    doResendVerificationCode(email);
  };

  const submitDisabled = isVerifying || formState.isSubmitting;
  const resendDisabled = cooldownRemaining > 0 || isResending;

  return (
    <ColocaForm form={form} onSubmit={onSubmit} className="w-full max-w-md">
      <div className="bg-card border rounded-xl py-6 px-5 w-full flex flex-col gap-5">
        {verifyError && (
          <ColocaAlert variant="destructive" icon={AlertCircle} text={translateVerifyError(verifyError, t)} />
        )}

        <ColocaOtpField
          name="code"
          label={t("verify_code_label")}
          length={OTP_LENGTH}
          autoFocus
          onComplete={(code) => {
            if (submitDisabled) return;
            clearVerifyError();
            doVerifyEmail({ email, code });
          }}
        />

        <ColocaButton
          type="submit"
          variant="primary"
          iconTrailing={ArrowRight}
          loading={isVerifying}
          disabled={submitDisabled}
          className="w-full"
        >
          {isVerifying ? t("verify_submitting") : t("verify_submit_button")}
        </ColocaButton>

        <ColocaButton
          type="button"
          variant="secondary"
          icon={RefreshCw}
          loading={isResending}
          disabled={resendDisabled}
          onClick={handleResend}
          className="w-full"
        >
          {cooldownRemaining > 0
            ? t("verify_resend_cooldown", { seconds: cooldownRemaining })
            : t("verify_resend_button")}
        </ColocaButton>
      </div>
    </ColocaForm>
  );
}
